import { FIELD_TYPES } from 'constants/fieldTypes';
import {
  SUCCESSOR_FORM_MODE,
  SUCCESSOR_PERSON_SOURCE,
} from 'constants/succession';
import { isSuccessionUserRemoved } from 'utility/succession';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from 'utility/validation';

const { ADD, EDIT, REMOVE, READD } = SUCCESSOR_FORM_MODE;
const { EMPLOYEES, POOL } = SUCCESSOR_PERSON_SOURCE;
const NOTE_MAX_LENGTH = 1000;

const { TEXT, SELECT, BUTTON_SWITCH, NUMBER_STEPPER, USER_AVATAR, READ_ONLY } =
  FIELD_TYPES;

export const prepareSuccessorFormFields = ({
  labels,
  mode,
  successor,
  onPoolChange,
}) => {
  if (mode === REMOVE) {
    const isViewingRemoved = isSuccessionUserRemoved(successor?.status);

    if (isViewingRemoved) {
      return [
        {
          name: 'successor',
          translationKey: 'successor',
          type: USER_AVATAR,
          avatarProps: {
            variant: 'subtitle1',
            caption: true,
            small: true,
          },
        },
        {
          name: 'reason',
          type: READ_ONLY,
          content: 'reasonLabel',
        },
        {
          name: 'comment',
          type: READ_ONLY,
          content: 'removalComment',
        },
      ];
    }

    return [
      {
        name: 'successor',
        translationKey: 'successor',
        type: USER_AVATAR,
        avatarProps: {
          variant: 'subtitle1',
          caption: true,
          small: true,
        },
      },
      {
        name: 'reason',
        type: SELECT,
        optionsKey: 'removalReasons',
        isSearchDisabled: true,
        required: true,
        parser: { value: 'id', label: 'label' },
        validators: [{ type: 'required', validator: isEmpty }],
      },
      {
        name: 'comment',
        type: TEXT,
        multiline: true,
        rows: 4,
        validators: [
          {
            type: 'forbiddenCharacters',
            validator: forbiddenCharacters,
          },
          {
            type: 'maxLength',
            validator: value => validateLength(value, 0, NOTE_MAX_LENGTH),
          },
        ],
      },
    ];
  }

  const fields = [];

  if (!successor && mode === ADD) {
    fields.push(
      {
        name: 'source',
        type: BUTTON_SWITCH,
        items: [
          { value: EMPLOYEES, label: labels.employees },
          { value: POOL, label: labels.fromPool },
        ],
        dependants: [
          {
            name: 'employee',
            resetFnc: async (_previousSource, nextSource, employee) =>
              nextSource === POOL ? null : employee,
          },
          {
            name: 'pool_member',
            manualResetFn: async ({
              currentFieldValue,
              setFieldValue,
              setFieldError,
            }) => {
              if (currentFieldValue !== EMPLOYEES) return;
              await onPoolChange(null);
              setFieldValue('pool_member', null);
              setFieldValue('pool', null);
              setFieldError('pool_member', null);
              setFieldError('pool', null);
            },
          },
        ],
      },
      {
        name: 'employee',
        translationKey: 'successor',
        type: SELECT,
        optionsKey: 'employees',
        isUser: true,
        isToggleable: true,
        shouldRender: values => values.source === EMPLOYEES,
        shouldFadeOutInstant: true,
        required: true,
        shouldReturnOption: true,
        parser: { value: 'id', label: 'first_name' },
        validators: [
          {
            type: 'required',
            shouldPassFormValues: true,
            validator: (value, values) => {
              if (values?.source !== EMPLOYEES) return true;
              return isEmpty(value);
            },
          },
        ],
      },
      {
        name: 'pool',
        type: SELECT,
        optionsKey: 'pools',
        isToggleable: true,
        shouldRender: values => values.source === POOL,
        shouldFadeOutInstant: true,
        required: true,
        parser: { value: 'id', label: 'name' },
        validators: [
          {
            type: 'required',
            shouldPassFormValues: true,
            validator: (value, values) => {
              if (values?.source !== POOL) return true;
              return isEmpty(value);
            },
          },
        ],
        dependants: [
          {
            name: 'pool_member',
            resetFnc: async (_previous, poolId) => {
              await onPoolChange(poolId);
              return null;
            },
          },
        ],
      },
      {
        name: 'pool_member',
        translationKey: 'successor',
        type: SELECT,
        optionsKey: 'poolMembers',
        isUser: true,
        isToggleable: true,
        shouldRender: values => values.source === POOL,
        shouldFadeOutInstant: true,
        required: true,
        shouldReturnOption: true,
        parser: { value: 'id', label: 'first_name' },
        validators: [
          {
            type: 'required',
            shouldPassFormValues: true,
            validator: (value, values) => {
              if (values?.source !== POOL) return true;
              return isEmpty(value);
            },
          },
        ],
      }
    );
  } else if (successor) {
    fields.push({
      name: 'successor',
      translationKey: 'successor',
      type: USER_AVATAR,
      avatarProps: {
        variant: 'subtitle1',
        caption: true,
        small: true,
      },
    });
  }

  fields.push(
    {
      name: 'readiness_level',
      translationKey: 'readiness',
      type: SELECT,
      optionsKey: 'readinessOptions',
      isSearchDisabled: true,
      required: true,
      parser: { value: 'id', label: 'label' },
      validators: [{ type: 'required', validator: isEmpty }],
    },
    {
      name: 'rank',
      translationKey: 'rank',
      type: NUMBER_STEPPER,
      required: true,
      validators: [
        {
          type: 'minValue',
          validator: value =>
            Number.isInteger(Number(value)) && Number(value) >= 1,
        },
      ],
    },
    {
      name: 'note',
      type: TEXT,
      multiline: true,
      rows: 4,
      validators: [
        {
          type: 'forbiddenCharacters',
          validator: forbiddenCharacters,
        },
        {
          type: 'maxLength',
          validator: value => validateLength(value, 0, NOTE_MAX_LENGTH),
        },
      ],
    }
  );

  return fields;
};

export const successorFormInitialData = ({ mode, successor }) => ({
  id: mode === EDIT || mode === READD ? successor?.id : undefined,
  successor: successor?.employee || {},
  source: EMPLOYEES,
  employee: null,
  pool: '',
  pool_member: null,
  readiness_level: successor?.readiness?.id,
  rank: successor?.rank ?? 1,
  note: successor?.note,
  reason: successor?.removal_reason?.id || '',
  reasonLabel: successor?.removal_reason?.label || '-',
  comment: successor?.removal_comment,
  removalComment: successor?.removal_comment || '-',
});

export const preparePoolMembers = poolMembers => {
  return (poolMembers || []).reduce((acc, member) => {
    return [...acc, { ...member.employee }];
  }, []);
};
